import { useRef, useState } from 'react';
import type { ProductItem } from '@/mocks/products';

interface ProductCarouselProps {
  categoryName: string;
  products: ProductItem[];
}

export default function ProductCarousel({ categoryName, products }: ProductCarouselProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 10);
  };

  const scroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const scrollAmount = 300;
    el.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
    setTimeout(checkScroll, 300);
  };

  const truncate = (str: string, maxLen = 48) => {
    if (str.length <= maxLen) return str;
    return str.slice(0, maxLen).trim() + '...';
  };

  return (
    <div className="mb-12 md:mb-16">
      <div className="flex items-center justify-between mb-5 md:mb-6">
        <h3 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900">
          {categoryName}
        </h3>
        <div className="hidden md:flex items-center gap-2">
          <button
            onClick={() => scroll('left')}
            disabled={!canScrollLeft}
            className="w-9 h-9 rounded-full border border-background-300 flex items-center justify-center text-foreground-500 hover:border-accent-400 hover:text-accent-600 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <i className="ri-arrow-left-s-line text-lg" />
          </button>
          <button
            onClick={() => scroll('right')}
            disabled={!canScrollRight}
            className="w-9 h-9 rounded-full border border-background-300 flex items-center justify-center text-foreground-500 hover:border-accent-400 hover:text-accent-600 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <i className="ri-arrow-right-s-line text-lg" />
          </button>
        </div>
      </div>

      <div
        ref={scrollRef}
        onScroll={checkScroll}
        className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 scrollbar-hide"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {products.map((product) => (
          <a
            key={product.id}
            href={product.link}
            target="_blank"
            rel="noopener nofollow"
            data-product-shop
            className="group shrink-0 w-[200px] md:w-[240px] snap-start"
          >
            <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-accent-300/50 transition-all duration-300 hover:shadow-md">
              <div className="aspect-square overflow-hidden bg-background-100">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
              <div className="p-3 md:p-4">
                <p className="text-xs md:text-sm font-medium text-foreground-800 leading-snug mb-2 line-clamp-2 min-h-[2.5rem]">
                  {truncate(product.name, 52)}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-sm font-semibold text-accent-600">{product.priceRMB}</span>
                    <span className="text-xs text-foreground-400">{product.priceUSD}</span>
                  </div>
                  <span className="text-xs font-medium text-accent-500 bg-accent-50 px-2 py-1 rounded-md group-hover:bg-accent-100 transition-colors">
                    Buy
                  </span>
                </div>
              </div>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}